import { MONEY_SCALE, parseHumanAmount, ZERO_MONEY } from '@aeris/money';
import { exchangeFor, quoteFor, receiptFor, settlementReceiptFor, statementFor, } from '@aeris/printing';
/**
 * A venda inventada que a prévia do modelo da nota imprime (F149).
 *
 * Passa pelos **construtores de verdade**: se a prévia montasse o papel por um
 * caminho próprio, ela mentiria no dia em que um construtor mudasse — é a mesma
 * regra do editor de etiqueta, e o motivo está escrito em `etiquetas.tsx`.
 *
 * O exemplo cobre tudo o que o modelo liga e desliga: dois itens, um deles com
 * preço alterado, desconto, forma de pagamento, troco, operador, caixa e
 * número, para o código de barras aparecer.
 */
const money = (text) => parseHumanAmount(text, MONEY_SCALE) ?? ZERO_MONEY;
const AT = new Date(2026, 8, 16, 14, 32);
/** "IMPRESSÃO DE TESTE" para um papel esquecido no balcão não virar venda. */
const CUSTOMER = 'IMPRESSÃO DE TESTE';
/*
 * Um cliente de mentira COM todos os dados (F160).
 *
 * Sem isto, ligar "Endereço do cliente" no editor não mudaria nada na prévia —
 * e a pessoa concluiria que a chave não funciona. Os valores são obviamente de
 * exemplo, para ninguém confundir com um cadastro de verdade.
 */
const SAMPLE_CUSTOMER = {
    document: '000.000.000-00',
    phone: '(00) 00000-0000',
    address: 'Rua de Exemplo, 123 — Centro, Cidade/UF',
    email: 'cliente@exemplo.com',
    // F161: o que o papel do sistema antigo trazia.
    code: '000123',
    phones: [
        { label: 'Telefone', value: '(00) 0000-0000' },
        { label: 'Celular', value: '(00) 00000-0000' },
    ],
    addressParts: {
        street: 'Rua de Exemplo',
        number: '123',
        district: 'Centro',
        complement: 'Sala 2',
        postalCode: '00000-000',
        cityState: 'Cidade - UF',
    },
};
export const sampleDocumentFor = (key, template, company, branchName) => {
    const lines = [
        {
            code: '008520',
            location: 'GAVETA 2',
            description: 'Cabo USB-C 1m trançado',
            quantity: '2',
            unit: 'UN',
            unitPrice: money('25,00'),
            lineTotal: money('50,00'),
        },
        {
            code: '005775',
            location: 'VITRINE A',
            description: 'Película de vidro',
            quantity: '1',
            unit: 'UN',
            unitPrice: money('30,00'),
            lineTotal: money('30,00'),
            discount: money('5,00'),
            priceOverridden: true,
        },
    ];
    switch (key) {
        case 'receipt':
            return receiptFor({
                company,
                branchName,
                number: 9999,
                issuedAt: AT,
                customerName: CUSTOMER,
                customer: SAMPLE_CUSTOMER,
                operatorName: 'Ana',
                terminal: 'CAIXA-01',
                lines,
                subtotal: money('80,00'),
                discount: money('5,00'),
                total: money('75,00'),
                payments: [{ method: 'Cartão de débito', amount: money('75,00') }],
                changeDue: ZERO_MONEY,
                documentId: '019f0000-0000-7000-8000-000000000001',
                template,
            });
        case 'quote':
            return quoteFor({
                company,
                branchName,
                number: 9999,
                issuedAt: AT,
                validUntil: new Date(2026, 8, 23),
                customerName: CUSTOMER,
                customer: SAMPLE_CUSTOMER,
                salespersonName: 'Ana',
                lines,
                subtotal: money('80,00'),
                discount: money('5,00'),
                total: money('75,00'),
                reservesStock: true,
                documentId: '019f0000-0000-7000-8000-000000000002',
                template,
            });
        case 'exchange':
        case 'voucher':
            return exchangeFor({
                kind: key === 'voucher' ? 'voucher' : 'exchange',
                company,
                branchName,
                number: 9999,
                issuedAt: AT,
                customerName: CUSTOMER,
                originalSaleNumber: 1234,
                handledByName: 'Ana',
                returned: [
                    {
                        description: 'Cabo USB-C 1m trançado',
                        quantity: '1',
                        unit: 'UN',
                        unitPrice: money('25,00'),
                        lineTotal: money('25,00'),
                        restocked: false,
                    },
                ],
                outgoing: key === 'voucher'
                    ? []
                    : [
                        {
                            description: 'Película de vidro',
                            quantity: '1',
                            unit: 'UN',
                            unitPrice: money('30,00'),
                            lineTotal: money('30,00'),
                        },
                    ],
                returnedTotal: money('25,00'),
                outgoingTotal: key === 'voucher' ? ZERO_MONEY : money('30,00'),
                voucherCredit: key === 'voucher' ? money('25,00') : ZERO_MONEY,
                receivable: key === 'voucher' ? ZERO_MONEY : money('5,00'),
                balanceAfter: key === 'voucher' ? money('25,00') : ZERO_MONEY,
                note: null,
                exchangeId: '019f0000-0000-7000-8000-000000000003',
                template,
            });
        case 'statement':
            return statementFor({
                company,
                customer: { name: CUSTOMER, document: '123.456.789-09' },
                asOf: AT,
                entries: [
                    {
                        dueAt: new Date(2026, 8, 10),
                        description: 'Venda nº 1234 — parcela 1/2',
                        daysOverdue: 6,
                        outstanding: money('75,00'),
                    },
                ],
                overdueTotal: money('75,00'),
                upcomingTotal: money('75,00'),
                total: money('150,00'),
                payments: [
                    {
                        settledOn: new Date(2026, 8, 2),
                        description: 'Venda nº 1200',
                        paid: money('50,00'),
                        method: 'Dinheiro',
                    },
                ],
                template,
            });
        case 'settlement':
            return settlementReceiptFor({
                company,
                customer: { name: CUSTOMER, document: '123.456.789-09' },
                entryDescription: 'Venda nº 1234 — parcela 1/2',
                settledOn: AT,
                amount: money('75,00'),
                interest: ZERO_MONEY,
                fine: ZERO_MONEY,
                discount: ZERO_MONEY,
                paid: money('75,00'),
                method: 'Dinheiro',
                receivedBy: 'Ana',
                remaining: money('75,00'),
                settlementId: '019f0000-0000-7000-8000-000000000004',
                reversed: false,
                template,
            });
    }
};
//# sourceMappingURL=receipt-sample.js.map